/* src/pages/SolutionsPage.js */
import React, { useContext, useMemo } from "react";
import { Link } from "react-router-dom";
import "./SolutionsPage.css";
import Seo from "../components/Seo";
import { MODE, getTEXT, MARKETING_LABEL } from "../config/compliance";
import { LangContext } from "../lang/LangProvider";

// Collegamenti di approfondimento: ogni metodologia rimanda alla serie AlphaFrame Lab
// (dove è raccontata in dettaglio) o alla Knowledge Base.
const LINKS = {
  dl: { to: "/series/lab/i-motori", it: "Approfondisci nella serie Lab: I motori →", en: "More in the Lab series: The engines →" },
  validation: { to: "/series/lab/il-metodo", it: "Approfondisci: Il metodo e il gate →", en: "More: The method and the gate →" },
  permanent: { to: "/series/lab/cosa-e-sopravvissuto", it: "Approfondisci: cosa è sopravvissuto →", en: "More: what survived →" },
};

/** === Contenuti IT (originali) === */
const STRATEGIES_IT = [
  {
    id: "dl",
    name: "Deep Learning per l'allocazione",
    tagline: "Reti neurali addestrate a stimare l'allocazione tra classi di ETF, con attenzione a imposte e costi",
    bullets: [
      "Reti LSTM con meccanismo di attenzione su un ampio set di variabili di mercato e macro",
      "Ottimizzazione walk-forward al netto della tassa del 26% e dei costi di transazione reali",
      "Validazione out-of-sample con regolarizzazione per contenere l'over-fitting",
    ],
    flow: ["Ingestione dati", "Feature engineering", "Addestramento walk-forward", "Allocazione netta"],
    paperRef: "Bao, Yue & Rao (2017); Gu, Kelly & Xiu (2020); Dixon, Halperin & Bilokon (2020)",
    warning: "L'esito dipende dall'accuratezza del modello; nessuna garanzia di risultato. Il verdetto fuori campione è ancora in corso.",
  },
  {
    id: "validation",
    name: "Validazione rigorosa e anti-overfitting",
    tagline: "Il metodo che decide se un modello merita fiducia — prima di guardare i rendimenti",
    bullets: [
      "Gate distribuzionale su blocchi train / test / validation, sempre al netto delle imposte",
      "Deflated Sharpe Ratio e Probability of Backtest Overfitting per correggere il numero di tentativi",
      "Consapevolezza del decadimento out-of-sample: un backtest brillante non è una promessa",
    ],
    flow: ["Molte ipotesi", "Gate distribuzionale", "Correzione multiple-testing", "Verdetto (anche KILL)"],
    paperRef: "Bailey & López de Prado (2014); Bailey, Borwein, López de Prado & Zhu (2017); Harvey, Liu & Zhu (2016); McLean & Pontiff (2016)",
    warning: "Nessun metodo elimina il rischio di falsi positivi: lo riduce. Il rigore serve a scartare, non a promettere.",
  },
  {
    id: "permanent",
    name: "Permanent Portfolio",
    tagline: "Allocazione bilanciata e stabile, a basso costo di gestione",
    bullets: [
      "Diversificazione tra azioni, obbligazioni, oro e liquidità in pesi stabili",
      "Ribilanciamento periodico verso l'allocazione target",
      "Progettato per contenere costi di transazione e impatto fiscale",
    ],
    flow: ["Pesi target", "Ribilanciamento periodico", "Monitoraggio", "Revisione"],
    paperRef: "Harry Browne, «Fail-Safe Investing» (1999)",
    warning: "Possibile sottoperformance nelle fasi di forte crescita azionaria.",
  },
];

/** === Traduzione EN === */
const STRATEGIES_EN = [
  {
    id: "dl",
    name: "Deep Learning for allocation",
    tagline: "Neural networks trained to estimate allocation across ETF classes, mindful of taxes and costs",
    bullets: [
      "LSTM networks with attention over a wide set of market and macro variables",
      "Walk-forward optimization net of the Italian 26% tax and real transaction costs",
      "Out-of-sample validation with regularization to contain over-fitting",
    ],
    flow: ["Data ingestion", "Feature engineering", "Walk-forward training", "Net allocation"],
    paperRef: "Bao, Yue & Rao (2017); Gu, Kelly & Xiu (2020); Dixon, Halperin & Bilokon (2020)",
    warning: "The outcome depends on model accuracy; no result is guaranteed. The out-of-sample verdict is still in progress.",
  },
  {
    id: "validation",
    name: "Rigorous validation & anti-overfitting",
    tagline: "The method that decides whether a model deserves trust — before looking at returns",
    bullets: [
      "Distributional gate on train / test / validation blocks, always net of taxes",
      "Deflated Sharpe Ratio and Probability of Backtest Overfitting to correct for the number of trials",
      "Awareness of out-of-sample decay: a brilliant backtest is not a promise",
    ],
    flow: ["Many hypotheses", "Distributional gate", "Multiple-testing correction", "Verdict (incl. KILL)"],
    paperRef: "Bailey & López de Prado (2014); Bailey, Borwein, López de Prado & Zhu (2017); Harvey, Liu & Zhu (2016); McLean & Pontiff (2016)",
    warning: "No method removes the risk of false positives: it reduces it. Rigor is there to reject, not to promise.",
  },
  {
    id: "permanent",
    name: "Permanent Portfolio",
    tagline: "A balanced, stable allocation with low management cost",
    bullets: [
      "Diversification across stocks, bonds, gold and cash with stable weights",
      "Periodic rebalancing toward the target allocation",
      "Designed to contain transaction costs and tax impact",
    ],
    flow: ["Target weights", "Periodic rebalancing", "Monitoring", "Review"],
    paperRef: "Harry Browne, “Fail-Safe Investing” (1999)",
    warning: "Possible underperformance during strong equity growth phases.",
  },
];

const SolutionsPage = () => {
  const { lang } = useContext(LangContext);

  const STRATEGIES = useMemo(
    () => (lang === "en" ? STRATEGIES_EN : STRATEGIES_IT),
    [lang]
  );

  const seoTitle =
    lang === "en" ? "Quantitative methodologies" : "Metodologie quantitative";

  const seoDesc =
    lang === "en"
      ? "Quantitative methodologies with paper sources: deep learning for allocation, rigorous validation & anti-overfitting, economic-regime allocation, permanent portfolio. Educational; not investment advice or a return promise."
      : "Metodologie quantitative con fonti scientifiche: deep learning per l'allocazione, validazione rigorosa e anti-overfitting, allocazione per regime, permanent portfolio. Contenuto educativo; non è consulenza né promessa di rendimento.";

  const pageTitle =
    MODE === "PRE"
      ? lang === "en"
        ? "Methods & Case Studies (educational)"
        : "Metodologie e Casi Studio (educational)"
      : lang === "en"
      ? "Quantitative Methods"
      : "Metodologie Quantitative";

  // Testi per lingua dal config (prima l'EN era hardcoded e il disclaimer usciva sempre in IT)
  const TEXT_CONF = getTEXT(lang);
  const intro = TEXT_CONF[MODE]?.solutionsIntro || "";

  return (
    <>
      <Seo
        title={seoTitle}
        description={seoDesc}
        path={`/${lang}/solutions`}
        image="/og/cover-solutions.png"
      />

      <div className="solutions-bg" aria-label={MARKETING_LABEL}>
        <section className="solutions-intro-premium">
          <h1>{pageTitle}</h1>
          <p>{intro}</p>
        </section>

        <section className="solutions-cards-list">
          {STRATEGIES.map((s) => (
            <article key={s.id} className="solutions-card">
              <h2>{s.name}</h2>
              <h3 className="tagline">{s.tagline}</h3>
              <ul className="why-list">
                {s.bullets.map((b, i) => (
                  <li key={i}>{b}</li>
                ))}
              </ul>

              <div className="flowchart">
                {s.flow.map((step, idx) => (
                  <div className="step" key={idx}>
                    <span className="num">{idx + 1}</span>
                    <span className="label">{step}</span>
                  </div>
                ))}
              </div>

              <p className="solutions-paper">
                <em>{lang === "en" ? "References: " : "Riferimenti: "}{s.paperRef}</em>
              </p>

              <div className="warning-box">{s.warning}</div>

              {LINKS[s.id] && (
                <Link className="solutions-link" to={`/${lang}${LINKS[s.id].to}`}>
                  {lang === "en" ? LINKS[s.id].en : LINKS[s.id].it}
                </Link>
              )}
            </article>
          ))}
        </section>

        <footer className="solutions-disclaimer">
          {TEXT_CONF[MODE]?.pageDisclaimer}
        </footer>
      </div>
    </>
  );
};

export default SolutionsPage;
